/*
 * File: TagSuggestionModal.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 30, 2025 at 2:46 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import validator from 'validator';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation, Trans } from 'react-i18next';

// Utilities
import { toastMessage, toastSuccess } from '../../utilities/toaster';

// Services
import { suggestTag } from '../../services/tag';

// Components
import { Modal } from '../Modal';
import { Typography } from '../Typography';
import { Button } from '../Button';
import { TextArea } from '../TextArea';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../constants/base.constants';

// Styles
import * as S from './TagSuggestionModal.styles';


/**
 * Component
 */

export const TagSuggestionModal = ({
	isOpen, handleClose
}) => {

	// Create state
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [inputValues, setInputValues] = useState({});

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Create references
	const inputRef = useRef();

	// Get current UI mode from hook
	const uiMode = useSelector((state) => state.ui.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Get actions from hooks
	const { executeRecaptcha } = useGoogleReCaptcha();

	// Handle submission
	const submitSuggestion = async (e) => {

		// Prevent default
		e.preventDefault();

		// Get parameters
		const { suggestion } = inputValues;

		// Validate parameters
		if (!suggestion || validator.isEmpty(suggestion, { ignore_whitespace: true })) {

			// Show error
			toastMessage(uiMode, partner, t('tagSuggestionModal.missingSuggestionError'));

			// Return
			return;
		}

		// Update submission state
		setIsSubmitting(true);

		// Create parameters
		const params = { suggestion };

		// Add reCAPTCHA token
		try { params.recaptchaToken = await executeRecaptcha('submit_tag_suggestion'); } catch (_) {}

		// Attempt suggestion submission
		try {

			// Submit tag suggestion
			await suggestTag(params);

			// Update submission state
			setIsSubmitting(false);

			// Clear inputs
			setInputValues({});

			// Show success message
			toastSuccess(uiMode, partner, t('tagSuggestionModal.confirmationMessage'));

			// Close modal
			setTimeout(() => {
				handleClose();
			}, 2000);

		} catch (_) {

			// Update submission state
			setIsSubmitting(false);

			// Show error
			toastMessage(uiMode, partner, t('tagSuggestionModal.submissionError'));
		}
	};

	// Handle on input change action
	const handleOnChange = (event) => {
		const { name, value } = event.target;
		setInputValues({ ...inputValues, [name]: value });
	};

	// Handle actions on component open
	useEffect(() => {
		if (isOpen) {

			// Reset inputs
			setInputValues({});

			// Set timeout
			setTimeout(() => {

				// Focus input
				inputRef.current?.focus();

			}, 100);
		}
	}, [isOpen]);

	// Render component
	return (
		<Modal isOpen={isOpen} showOverlay handleClose={handleClose} clickOutsideClose={false}>
			<S.ModalInner>
				<S.Form>

					{/* Content */}
					<Typography tag="h3" weight="black">
						<Trans t={t} i18nKey="tagSuggestionModal.title">
							Suggest a Tag
						</Trans>
					</Typography>
					<Typography tag="p" variation="2">
						<Trans t={t} i18nKey="tagSuggestionModal.description">
							We are always happy to consider additional tags. Please submit your suggestion below and our review team will take a look.
						</Trans>
					</Typography>

					{/* Inputs */}
					<S.InputContainer>
						<TextArea
							placeholder={t('tagSuggestionModal.suggestionPlaceholder')}
							name="suggestion"
							type="text"
							characterLimit={150}
							defaultHideCharacterLimit
							rows={3}
							value={inputValues.suggestion || ''}
							onChange={handleOnChange}
							smartField
							ref={inputRef}
						/>
					</S.InputContainer>

					{/* Actions */}
					<Button disabled={isSubmitting} isLoading={isSubmitting} onClick={submitSuggestion}>
						<Trans t={t} i18nKey="actions.submit">
							Submit
						</Trans>
					</Button>
				</S.Form>
			</S.ModalInner>
		</Modal>
	);
};


/**
 * Configuration
 */

TagSuggestionModal.displayName = 'TagSuggestionModal';
TagSuggestionModal.propTypes = {
	isOpen: PropTypes.bool,
	handleClose: PropTypes.func,
};
TagSuggestionModal.defaultProps = {
	isOpen: false,
	handleClose: null,
};
